import React, { useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import Button from 'v3/components/Button';
import { formatDate } from './contract';
import ResizableTable from './ResizableTable';

export default function StageList({
  definition,
  columns,
  context,
  queryState,
  onPageChange,
  onRefresh,
  onClose,
  renderRowDetails,
}) {
  const heading = useRef(null);
  useEffect(() => {
    heading.current?.focus({ preventScroll: true });
  }, []);
  const { groups, loading, error } = queryState;
  const group = groups?.[definition.code];
  const page = context.query.pages[definition.code];
  const headingId =
    definition.code === 'AVAILABLE'
      ? 'tc-k-load-list-title'
      : `tc-k-${definition.code.toLowerCase()}-list-title`;
  return (
    <section
      className={definition.className}
      aria-labelledby={headingId}
      data-list-group={definition.code}
      aria-busy={loading}
    >
      <header className="tc-k-toolbar">
        <div className="tc-k-list-heading">
          <Button size="sm" variant="secondary" onClick={onClose}>
            <ChevronLeft size={16} aria-hidden="true" /> Voltar
          </Button>
          <div>
            <h1 id={headingId} ref={heading} tabIndex={-1}>
              {definition.title}
            </h1>
            <p className="tc-k-list-count">
              {group
                ? `${group.totalCount} ${definition.countDescription}`
                : 'Quantidade não consultada'}
            </p>
          </div>
        </div>
        <Button size="sm" variant="secondary" disabled={loading} onClick={onRefresh}>
          <RefreshCw size={15} aria-hidden="true" /> Atualizar lista
        </Button>
      </header>
      <p className="tc-k-timezone">
        Horários de Brasília · Arraste as divisórias do cabeçalho para ampliar as colunas.
      </p>
      {loading && (
        <p className="tc-k-empty" role="status">
          Carregando {definition.loadingLabel}…
        </p>
      )}
      {error && (
        <div className="tc-k-error" role="alert">
          <span>{error}</span>
          <Button size="sm" onClick={onRefresh}>
            Tentar novamente
          </Button>
        </div>
      )}
      <ResizableTable
        columns={columns}
        rows={group?.data || []}
        rowKey={(row) => row.key}
        offset={(page - 1) * context.query.perPage}
        label={definition.tableLabel}
        renderRowDetails={renderRowDetails}
      />
      {group && !loading && !error && (
        <>
          {group.data.length === 0 && (
            <p className="tc-k-empty" role="status">
              {group.totalCount ? 'Nenhum registro nesta página.' : definition.emptyMessage}
            </p>
          )}
          <footer className="tc-k-pagination tc-k-list-pagination">
            <div>
              <Button
                size="xs"
                variant="secondary"
                disabled={page <= 1}
                aria-label="Página anterior da lista"
                onClick={() => onPageChange(page - 1)}
              >
                <ChevronLeft size={16} aria-hidden="true" />
              </Button>
              <span>
                Página {page} de {group.pagination.totalPages || 1}
              </span>
              <Button
                size="xs"
                variant="secondary"
                disabled={!group.pagination.hasMore}
                aria-label="Próxima página da lista"
                onClick={() => onPageChange(page + 1)}
              >
                <ChevronRight size={16} aria-hidden="true" />
              </Button>
            </div>
            <span>
              {group.data.length} de {group.totalCount} registros · Consulta:{' '}
              {formatDate(group.asOf)}
            </span>
          </footer>
        </>
      )}
    </section>
  );
}
